import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react"
import { ChevronDown, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@f2e/ui/components/base"

import { AgentHistory } from "@/types"
import { AgentHistoryDropdownState } from "@/hooks/useAgentHistories"

export interface AgentHistoryDropdownBaseProps {
  agentId: string
  disabled?: boolean
  maxHeight?: string
  showPagination?: boolean
  renderItem?: (history: AgentHistory, isCurrent: boolean) => React.ReactNode
  renderTrigger?: (currentHistory: AgentHistory | null) => React.ReactNode
  filterHistories?: (histories: AgentHistory[]) => AgentHistory[]
  state: AgentHistoryDropdownState
}

export const AgentHistoryDropdownBase = forwardRef<
  {
    updateHistoryPublishStatus: (
      historyId: string,
      wasPublished: boolean
    ) => void
  },
  AgentHistoryDropdownBaseProps
>(
  (
    {
      disabled = false,
      maxHeight = "max-h-80",
      showPagination = true,
      renderItem,
      renderTrigger,
      state,
      filterHistories,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [scrollPosition, setScrollPosition] = useState(0)
    const dropdownContentRef = useRef<HTMLDivElement>(null)

    // Use provided state directly - no more fallback logic
    const {
      histories,
      isLoadingInitial: stateIsLoadingInitial,
      isLoadingMore: stateIsLoadingMore,
      hasMore: stateHasMore,
      status: stateStatus,
      loadMore: stateLoadMore,
      updateHistoryPublishStatus: stateUpdateHistoryPublishStatus,
    } = state

    // Apply optional filter once per render for reuse across component
    const filteredHistories = useMemo(
      () => (filterHistories ? filterHistories(histories) : histories),
      [filterHistories, histories]
    )

    // Expose the updateHistoryPublishStatus function via ref
    useImperativeHandle(
      ref,
      () => ({
        updateHistoryPublishStatus: stateUpdateHistoryPublishStatus,
      }),
      [stateUpdateHistoryPublishStatus]
    )

    // Preserve scroll position when dropdown opens
    React.useEffect(() => {
      if (scrollPosition > 0 && dropdownContentRef.current) {
        setTimeout(() => {
          if (dropdownContentRef.current) {
            dropdownContentRef.current.scrollTop = scrollPosition
          }
        }, 0)
      }
    }, [scrollPosition])

    // Restore scroll position after loading more histories
    React.useEffect(() => {
      if (
        scrollPosition > 0 &&
        dropdownContentRef.current &&
        !stateIsLoadingMore
      ) {
        // Use setTimeout to ensure DOM has updated
        setTimeout(() => {
          if (dropdownContentRef.current) {
            dropdownContentRef.current.scrollTop = scrollPosition
          }
          setScrollPosition(0) // Reset after restoring
        }, 0)
      }
    }, [filteredHistories, stateIsLoadingMore, scrollPosition])

    // Find current history
    const currentHistory = useMemo(() => {
      if (
        state?.currentHistory?.id &&
        filteredHistories.some((h) => h.id === state.currentHistory!.id)
      ) {
        return state.currentHistory
      }
      // Fallback to first available history in filtered list
      return filteredHistories.length > 0 ? filteredHistories[0] : null
    }, [state?.currentHistory, filteredHistories])

    const handleHistorySelect = (history: AgentHistory) => {
      setOpen(false)
      state.setCurrentHistoryId(history.id)
    }

    // Load more histories with scroll position preservation
    const handleLoadMore = useCallback(
      async (event?: React.MouseEvent) => {
        if (!stateHasMore || stateIsLoadingMore) return

        // Prevent dropdown from closing
        event?.stopPropagation()
        event?.preventDefault()

        // Store current scroll position
        if (dropdownContentRef.current) {
          const currentScrollPosition = dropdownContentRef.current.scrollTop
          setScrollPosition(currentScrollPosition)
        }

        await stateLoadMore()
      },
      [stateHasMore, stateIsLoadingMore, stateLoadMore]
    )

    // Default render functions
    const defaultRenderItem = (history: AgentHistory, isCurrent: boolean) => (
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2 flex-1">
          <span className="text-sm font-medium">
            {t("workspace.agent.history.ui.versionNumber", {
              number: history.versionNumber,
            })}
          </span>
          {history.wasPublished && (
            <span className="text-xs text-yellow-600">★</span>
          )}
          {isCurrent && (
            <span className="text-xs text-muted-foreground">
              {t("workspace.agent.history.ui.current")}
            </span>
          )}
        </div>
      </div>
    )

    const defaultRenderTrigger = (currentHistory: AgentHistory | null) => (
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        className="h-8 px-3 gap-2"
      >
        <span className="text-sm font-medium">
          {currentHistory
            ? t("workspace.agent.history.ui.versionNumber", {
                number: currentHistory.versionNumber,
              })
            : t("workspace.agent.history.loading.versions")}
        </span>
        <ChevronDown className="h-3 w-3" />
      </Button>
    )

    // Render dropdown content based on state
    const renderDropdownContent = () => {
      if (stateStatus === "loading" && stateIsLoadingInitial) {
        return (
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">
                {t("workspace.agent.history.loading.versions")}
              </span>
            </div>
          </div>
        )
      }

      if (state.historiesError) {
        return (
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center gap-2 text-red-500">
              <span className="text-sm">
                {t("workspace.agent.history.errors.loadVersions.title")}
              </span>
            </div>
          </div>
        )
      }

      if (filteredHistories.length === 0) {
        return (
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <span className="text-sm">
                {t("workspace.agent.history.ui.noVersionsAvailable")}
              </span>
            </div>
          </div>
        )
      }

      return (
        <>
          {filteredHistories.map((history, index) => {
            const isCurrent = history.id === currentHistory?.id
            return (
              <DropdownMenuItem
                key={`${history.id}-${index}`}
                onClick={() => handleHistorySelect(history)}
                className="py-2"
              >
                {renderItem
                  ? renderItem(history, isCurrent)
                  : defaultRenderItem(history, isCurrent)}
              </DropdownMenuItem>
            )
          })}

          {/* Load More Button */}
          {showPagination &&
            ((stateHasMore && !stateIsLoadingMore) || stateIsLoadingMore) && (
              <DropdownMenuItem
                onSelect={(e) => {
                  // Prevent dropdown from closing
                  e.preventDefault()
                  handleLoadMore()
                }}
                disabled={stateIsLoadingMore}
                className="flex items-center justify-center"
              >
                {stateIsLoadingMore ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin mr-2" />
                    <span className="text-xs">
                      {t("workspace.agent.history.loading.versions")}
                    </span>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {t("workspace.agent.history.ui.loadMoreVersions")}
                  </span>
                )}
              </DropdownMenuItem>
            )}
        </>
      )
    }

    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          {renderTrigger
            ? renderTrigger(currentHistory)
            : defaultRenderTrigger(currentHistory)}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className={`w-64 ${maxHeight} overflow-y-auto`}
          ref={dropdownContentRef}
        >
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            {t("workspace.agent.versionHistory")}
          </div>
          {renderDropdownContent()}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
)
